import { useMemo, useState } from 'react'
import { Box, Flex, Skeleton } from '@mantine/core'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { Button } from '@/components/Button/Button'
import { ManageBudgetsList } from '@/components/ManageBudgetsList/ManageBudgetsList'
import type {
  EditedMonthlyAmounts,
  ManageBudgetsModalProps,
} from '@/components/ManageBudgetsModal/ManageBudgetsModal.types'
import { Modal } from '@/components/Modal/Modal'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import {
  useBankingServiceBankingControllerGetIncomeStats,
  useBudgetServiceBudgetControllerDeleteBudget,
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  useBudgetServiceBudgetControllerUpdateBudget,
} from '@/__generated__/queries'

import { INSIGHTS_QUERY_KEY } from '@/hooks/useInsightsQuery.hook'

import { formatMoney } from '@/helpers/formatMoney.helper'

import classes from './ManageBudgetsModal.module.css'

const INVALIDATE_KEYS = [
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  INSIGHTS_QUERY_KEY,
]

export function ManageBudgetsModal({
  budgets,
  onClose,
  opened,
}: ManageBudgetsModalProps) {
  const queryClient = useQueryClient()

  const isMutating = useIsMutating({
    exact: true,
  })

  const now = dayjs()

  const editedMonthlyAmountsSet = new Set<EditedMonthlyAmounts>()
  const deletedBudgetsSet = new Set<number>()

  const [editedMonthlyAmounts, setEditedMonthlyAmounts] = useState(
    editedMonthlyAmountsSet,
  )

  const [deletedBudgets, setDeletedBudgets] = useState(deletedBudgetsSet)

  const { data, isFetching, isLoading } =
    useBankingServiceBankingControllerGetIncomeStats({
      endDate: now.endOf('month').toISOString(),
      startDate: now.startOf('month').toISOString(),
    })

  const editBudgetMutation = useBudgetServiceBudgetControllerUpdateBudget()
  const deleteBudgetMutation = useBudgetServiceBudgetControllerDeleteBudget()

  const totalIncome = useMemo(() => {
    if (!data) {
      return 0
    }

    return data.value
  }, [data])

  const addEditedMonthlyAmount = ({
    budgetId,
    value,
  }: EditedMonthlyAmounts): void => {
    setEditedMonthlyAmounts(prev => {
      const newSet = new Set(prev)

      newSet.forEach(item => {
        if (item.budgetId === budgetId) {
          newSet.delete(item)
        }
      })

      newSet.add({ budgetId, value })

      return newSet
    })
  }

  const addBudgetToDelete = (budgetId: number): void => {
    setDeletedBudgets(prev => {
      const newSet = new Set(prev)

      newSet.add(budgetId)

      return newSet
    })
  }

  const handleBudgetsSave = async () => {
    const editedPromises = Array.from(editedMonthlyAmounts).map(async item => {
      if (deletedBudgets.has(item.budgetId)) {
        return
      }

      await editBudgetMutation.mutateAsync({
        budgetId: item.budgetId,
        requestBody: {
          category: budgets.budgets.find(budget => budget.id === item.budgetId)
            ?.category!,
          monthlyAmount: item.value,
        },
      })
    })

    const deletePromises = Array.from(deletedBudgets).map(async budgetId => {
      await deleteBudgetMutation.mutateAsync({
        budgetId,
      })
    })

    await Promise.all([...editedPromises, ...deletePromises])

    INVALIDATE_KEYS.forEach(key => {
      queryClient.invalidateQueries({
        queryKey: [key],
      })
    })

    onClose()
  }

  const handleCancel = () => {
    setEditedMonthlyAmounts(new Set())
    setDeletedBudgets(new Set())

    onClose()
  }

  return (
    <Modal
      centered
      classNames={classes}
      onClose={onClose}
      opened={opened}
      withCloseButton={false}
    >
      <Box>
        <Flex align="center" direction="column">
          <Title mb={8} variant="h1">
            Manage budgets
          </Title>

          <Text align="center">
            Keep your spending in check to achieve your financial and personal
            goals!
          </Text>
        </Flex>

        <Flex
          direction={{
            base: 'column',
            md: 'row',
          }}
          gap={16}
          mt={32}
        >
          <Box
            className={classes.budgetBox}
            w={{
              base: '100%',
              md: '50%',
            }}
          >
            <Text mb={8} variant="body_4_semibold">
              Current Monthly Income
            </Text>

            <Skeleton visible={isFetching || isLoading}>
              <Text variant="body_1_semibold">
                {formatMoney(totalIncome, 'USD')}
              </Text>
            </Skeleton>
          </Box>

          <Box
            className={classes.budgetBox}
            w={{
              base: '100%',
              md: '50%',
            }}
          >
            <Text mb={8} variant="body_4_semibold">
              Current Budget
            </Text>

            <Text variant="body_1_semibold">
              {formatMoney(budgets.totalBudget, 'USD')}
            </Text>
          </Box>
        </Flex>

        <ManageBudgetsList
          addBudgetToDelete={addBudgetToDelete}
          addEditedMonthlyAmount={addEditedMonthlyAmount}
          budgets={budgets}
        />

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={32}
        >
          <Button miw="48%" onClick={handleCancel} variant="outline">
            Cancel
          </Button>

          <Button
            loading={!!isMutating}
            miw="48%"
            onClick={handleBudgetsSave}
            type="submit"
          >
            Save
          </Button>
        </Flex>
      </Box>
    </Modal>
  )
}
